import {
  TravelMovementPerDayResponse,
  TravelMovementResponse
} from 'generated/index';
import { RouteResponse } from './components/TravelDetailDialogOverview';

interface DistancesByType {
  private: number;
  business: number;
}

export const sumInEuros = (sum: number) =>
  Number.isFinite(sum)
    ? Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR'
      }).format(sum)
    : '-';

export const calculateCompensationInEuros = (
  routes: { compensationInEuros?: number }[],
  isTravelMovements?: boolean
): number => {
  return routes.reduce((acc, route) => {
    if (isTravelMovements && 'compensationInEuros' in route) {
      if (route.compensationInEuros !== undefined) {
        acc += route.compensationInEuros;
      }
    }
    return acc;
  }, 0);
};

export const calculateManualDistanceInMeters = (
  routes: RouteResponse
): number => {
  return Array.isArray(routes)
    ? routes.reduce((total, route) => total + route.distanceInMeters, 0)
    : 0;
};

export const calculateAdjustedDistanceInMeters = (
  routes: RouteResponse,
  commuteDistanceInMeters: number
): number => {
  const manualDistanceInMeters = calculateManualDistanceInMeters(routes);
  return Math.max(manualDistanceInMeters - commuteDistanceInMeters, 0);
};

export const generateDistanceText = (
  distancesByType: DistancesByType,
  isManual: boolean,
  manualDistanceInMeters: number | null,
  isTravelMovements: boolean | undefined,
  t
): string => {
  if (isManual) {
    return manualDistanceInMeters !== null
      ? formatDistance(manualDistanceInMeters)
      : '';
  }

  if (isTravelMovements) {
    if (distancesByType.private && distancesByType.business) {
      return `${formatDistance(distancesByType.private)} (${t(
        'travelMovements.type.PRIVATE'
      )}) - ${formatDistance(distancesByType.business)} (${t(
        'travelMovements.type.BUSINESS'
      )})`;
    } else if (distancesByType.private) {
      return `${formatDistance(distancesByType.private)} (${t(
        'travelMovements.type.PRIVATE'
      )})`;
    } else {
      return `${formatDistance(distancesByType.business)} (${t(
        'travelMovements.type.BUSINESS'
      )})`;
    }
  }

  return (
    formatDistance(distancesByType.private + distancesByType.business) || '-'
  );
};

export const calculateDistancesByType = (
  routes: TravelMovementResponse[],
  isTravelMovements: boolean
): DistancesByType => {
  return routes.reduce(
    (acc: DistancesByType, route) => {
      const meters = route.correctedDistanceInMeters
        ? route.correctedDistanceInMeters
        : route.distanceInMeters;

      if (isTravelMovements && 'type' in route) {
        if (route.type === 'PRIVATE') {
          acc.private += meters;
        } else if (route.type === 'BUSINESS') {
          acc.business += meters;
        }
      } else {
        acc.business += meters;
      }

      return acc;
    },
    { private: 0, business: 0 }
  );
};

export const formatDistance = (distanceInMeters: number): string => {
  const distanceInKm = distanceInMeters / 1000;
  return Number.isInteger(distanceInKm)
    ? `${distanceInKm} km`
    : `${distanceInKm.toFixed(2).replace('.', ',')} km`;
};

export const formatDistanceRaw = (distanceInMeters: number) => {
  const distanceInKm = distanceInMeters;
  return Number.isInteger(distanceInKm) && distanceInKm / 1000;
};

export const calculateTotalKmByType = (
  rows: TravelMovementPerDayResponse[],
  type: string
) => {
  return formatDistance(
    rows.reduce((acc, row) => {
      // Calculate the total distance for the specified type
      const typeDistance = row.travelMovements
        .filter((route) => route.type === type)
        .reduce((typeAcc, route) => typeAcc + route.distanceInMeters, 0);

      // Add extra logic for 'isManual' rows
      const manualDistance =
        row.isManual && row.travelMovements.length > 0
          ? Math.max(
              row.travelMovements.reduce(
                (manualAcc, route) => manualAcc + route.distanceInMeters,
                0
              ) -
                (row.commuteDistanceInMeters ? row.commuteDistanceInMeters : 0),
              0
            )
          : 0;

      // Add manual distance to Business type totals, regardless of travelMovements type
      const additionalDistance = type === 'BUSINESS' ? manualDistance : 0;

      return acc + typeDistance + additionalDistance;
    }, 0)
  );
};
